import { Injectable, Inject, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BasicOrder } from "../models/basic-order.model";
import { environment } from "src/environments/environment";
import { TableService } from "src/app/_metronic/shared/crud-table";
import { Observable } from "rxjs";
import { RequestTypes } from "src/app/_metronic/shared/crud-table/models/request-types.enum";
import { OrderItem } from "../models/order-item.model";
import { Order } from "../models/order.model";

@Injectable({
  providedIn: "root",
})
export class OrdersService
  extends TableService<BasicOrder>
  implements OnDestroy
{
  BASE_URL = `${environment.apiUrl}`;
  GET_ALL_URL = "Order";
  GET_ONE_URL = "Order";
  DELETE_URL = "Order";
  private qrCodeInfo: any;

  constructor(
    @Inject(HttpClient) http,
    router: Router,
    activatedRoute: ActivatedRoute
  ) {
    super(http, router, activatedRoute);
  }

  acceptOrderItem(
    orderId: number,
    orderItemId: number,
    values: any
  ): Observable<boolean> {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/AcceptItem/${orderId}/${orderItemId}`,
      RequestTypes.POST,
      values
    );
  }

  editShippingData(item: OrderItem): Observable<boolean> {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/UpdateAcceptedItem/${item.orderId}/${item.id}`,
      RequestTypes.PUT,
      item
    );
  }

  setQrCodeInfo(qrcode: any) {
    this.qrCodeInfo = qrcode;
  }

  getQrCodeInfo() {
    return this.qrCodeInfo;
  }

  archive(orderId: number) {
    return this.http.put(
      `${this.BASE_URL}/${this.GET_ALL_URL}/Archive/${orderId}`,
      null
    );
  }

  unarchive(orderId: number) {
    return this.http.put(
      `${this.BASE_URL}/${this.GET_ALL_URL}/Unarchive/${orderId}`,
      null
    );
  }

  rejectOrderItem(orderId: number, orderItemId: number): Observable<boolean> {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/RejectItem/${orderId}/${orderItemId}`,
      RequestTypes.POST
    );
  }

  shipOrderItem(
    orderId: number,
    orderItemId: number,
    shippingCompany: string,
    shippingCode: string
  ) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/ShipItem/${orderId}/${orderItemId}/${shippingCompany}/${shippingCode}`,
      RequestTypes.POST
    );
  }

  instockOrderItem(orderId: number, orderItemId: number) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/InStockItem/${orderId}/${orderItemId}`,
      RequestTypes.POST
    );
  }

  editOrderItemData(formValues) {
    return this.http.post(
      `${this.BASE_URL}/${this.GET_ALL_URL}/EditOrderItemData`,
      formValues
    );
  }

  generatePaymentLink(orderId: number) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/GeneratePaymentLink/${orderId}`,
      RequestTypes.POST
    );
  }

  getMergableOrders(orderId) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/GetMergeableOrders/${orderId}`,
      RequestTypes.GET
    );
  }

  mergeOrder(orderId, selectedOrderId) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/MergeOrders/${orderId}/${selectedOrderId}`,
      RequestTypes.POST
    );
  }

  rejectAll(orderId) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/RejectItem/${orderId}/All`,
      RequestTypes.POST
    );
  }

  getActiveOrders(startDate: string, endDate: string, search: string) {
    let queryParams = new HttpParams();
    if (startDate) queryParams = queryParams.append("startDate", startDate);
    if (endDate) queryParams = queryParams.append("endDate", endDate);
    if (search) queryParams = queryParams.append("search", search);
    return this.http.get<Order[]>(
      `${this.BASE_URL}/${this.GET_ALL_URL}/GetActive`,
      {
        params: queryParams,
      }
    );
  }

  getOrderItemLogs(orderItemId: number) {
    return this.request(
      `${this.BASE_URL}/OrderLogs/Item/${orderItemId}`,
      RequestTypes.GET
    );
  }

  getOrderLogs(orderId: number) {
    return this.request(
      `${this.BASE_URL}/OrderLogs/Order/${orderId}`,
      RequestTypes.GET
    );
  }

  togglePaymentMethod(orderId: number) {
    return this.request(
      `${this.BASE_URL}/${this.GET_ALL_URL}/TogglePaymentType/${orderId}`,
      RequestTypes.PUT
    );
  }

  editRecipientInfo(formValues, orderId) {
    return this.http.put(
      `${this.BASE_URL}/${this.GET_ALL_URL}/EditRecipientInfo/${orderId}`,
      formValues
    );
  }

  changeDeliveryPrice(newDeliveryPrice, orderId) {
    return this.http.put(
      `${this.BASE_URL}/${this.GET_ALL_URL}/ChangeDeliveryPrice/${orderId}/${newDeliveryPrice}`,
      null
    );
  }

  specialDiscount(orderId, discountAmount) {
    let params = new HttpParams();
    params = params.append("specialDiscountAmount", discountAmount);
    return this.http.put(
      `${this.BASE_URL}/${this.GET_ALL_URL}/AddSpecialDiscount/${orderId}`,
      null,
      { params }
    );
  }

  createOrder(order: any) {
    let headers = new HttpHeaders();
    headers = headers.append("Currency", order.currency);
    headers = headers.append("Device-type", "4");
    return this.http.post(`${this.BASE_URL}/Cart/NoAccountPurchase`, order, {
      headers: headers,
    });
  }

  getOrderInvoiceData(orderId: number) {
    return this.request(
      `${this.BASE_URL}/Order/GetInvoiceDetails/${orderId}`,
      RequestTypes.GET
    );
  }
  captureInvoice(orderId: number) {
    return this.request(
      `${this.BASE_URL}/Order/CaptureInvoice/${orderId}`,
      RequestTypes.POST
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sb) => sb.unsubscribe());
  }
}
